import React, { useCallback, useEffect, useState } from "react";
import { FLOWABLE_BASE_URL } from "globals/constants";
import { useDictionary } from "hooks";
import { request } from "utils/proxy";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import PagingControls from "@cayuse/shared-react.components.paging-controls";
import Table from "components/Table";

import styles from "./styles.css";


const FormHistoryMenu = ({ formDefinitions, previewFormDefinitionId, updatePage, onRenderPreview }) => {
	const [ currentFormDefPageWithDeploymentTime, setCurrentFormDefPageWithDeploymentTime ] = useState([]);
	const [ formDefinitionsWithDeploymentTime, setFormDefinitionsWithDeploymentTime ] = useState([]);
	const [ pageOffset, setPageOffset ] = useState(0);
	const [ needsUpdatePage, setNeedsUpdatePage ] = useState(false);

	const dictionary = useDictionary("formManagerHistory");
	const pageLimit = 10;

	const setPaging = useCallback(({ items, pageNumber }) => {
		const pageOffset = pageNumber - 1;

		setPageOffset(pageOffset);

		items = items || formDefinitionsWithDeploymentTime;

		const currentItems = items.slice(pageOffset * pageLimit, (pageOffset * pageLimit) + pageLimit);

		setCurrentFormDefPageWithDeploymentTime(currentItems);
	}, [ formDefinitionsWithDeploymentTime ]);

	useEffect(() => {
		if (formDefinitionsWithDeploymentTime?.length <= 0) {
			getFormDefinitionsWithDeploymentTime()
				.then(formDefinitionsWithDeploymentTime => {
					setFormDefinitionsWithDeploymentTime(formDefinitionsWithDeploymentTime);

					setPaging({ items: formDefinitionsWithDeploymentTime, pageNumber: 1 });
				});
		}
	});

	useEffect(() => {
		const isOnSamePage = currentFormDefPageWithDeploymentTime
			.some(currentFormDefinitionsWithDeploymentTime => {
				return currentFormDefinitionsWithDeploymentTime.id === previewFormDefinitionId;
			});

		if (needsUpdatePage && previewFormDefinitionId && !isOnSamePage) {
			// Set page if update is needed
			const index = formDefinitionsWithDeploymentTime.findIndex(formDefinition => {
				return formDefinition.id === previewFormDefinitionId;
			});

			let newPageNumber = Math.floor(index / pageLimit) + 1;
			setPaging({ pageNumber: newPageNumber });
			setNeedsUpdatePage(false);
		}
	}, [
		needsUpdatePage,
		previewFormDefinitionId,
		currentFormDefPageWithDeploymentTime,
		formDefinitionsWithDeploymentTime,
		setPaging
	]);

	useEffect(() => {
		updatePage({
			updatePageFunction: () => {
				setNeedsUpdatePage(true);
			}
		});
	}, [
		updatePage
	]);

	const getFormDefinitionsWithDeploymentTime = async () => {
		const deploymentRequests = formDefinitions.map(({ isDraft, deploymentId }) => {
			return !isDraft
				? request(`${FLOWABLE_BASE_URL}/form-api/form-repository/deployments/${deploymentId}`)
				: false;
		});
		const deployments = await Promise.all(deploymentRequests);

		return formDefinitions.map(formDefinition => {
			const deployment = deployments.find(deployment => {
				return deployment.id === formDefinition.deploymentId;
			});

			return {
				...formDefinition,
				deploymentTime: deployment.deploymentTime
			};
		});
	};

	return (
		<>
			<Table captionText="Form History">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>
							{dictionary.tableHeadings?.revisions || "Revisions"}
						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>

				<Table.Body>
					{currentFormDefPageWithDeploymentTime?.map(formDefinition => {
						const isPreviewDef = formDefinition.id === previewFormDefinitionId;
						const rowClasses = isPreviewDef
							? `${styles.columnsContainerMenuRow} ${styles.columnsContainerMenuRowActive}`
							: styles.columnsContainerMenuRow;

						return (
							<Table.Row
								className={rowClasses}
								key={`${formDefinition.id}-${formDefinition.isDraft}`}
								onClick={() => onRenderPreview(formDefinition)}
							>
								<Table.Cell className={styles.columnsContainerMenuCell}>
									<strong>{dictionary.revision || "Revision"} {formDefinition.version}</strong><br/>
									{formDefinition.deploymentTime
										? (
											<span>
												{`${dictionary.publishedOn || "Published on"} `}
												<FormattedDatetime date={formDefinition.deploymentTime} />
											</span>
										)
										: <span>{dictionary.inDraftRevision || "In-Draft Revision"}</span>
									}
								</Table.Cell>
							</Table.Row>
						);
					})}
				</Table.Body>
			</Table>
			<div className={styles.pagingControls}>
				<PagingControls
					items={formDefinitionsWithDeploymentTime}
					totalItems={formDefinitionsWithDeploymentTime.length}
					pageLimit={pageLimit}
					pageOffset={pageOffset}
					showPageSizeControl={false}
					onPageChange={pageNumber => setPaging({ pageNumber })}
				/>
			</div>
		</>
	);
};

export default FormHistoryMenu;
